import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { loginCrmGuestUser, loginGuestUser } from "../actions/userAction";
import { ButtonSpinner } from "../components/Spinner";
import "./Login.css";

const SE_LOGO = "https://www.thesearcheducation.com/assets/img/SE-logo.webp";

// ---------------------------------------------------------------------------
// Left-panel copy for each login mode. Purely presentational data — swapping
// `loginMode` swaps this content (and, further down, which action gets
// dispatched on submit).
// ---------------------------------------------------------------------------
const BRAND_CONTENT = {
  guest: {
    eyebrow: "Guest Portal",
    headingLead: "Welcome to your",
    headingHighlight: "Guest Portal",
    copy: "Sign in with the email and access token shared with you to view your schedules, documents, and updates in real time.",
    features: [
      {
        label: "Secure token access",
        icon: (
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" stroke="currentColor" strokeWidth="1.6" />
            <path d="M7.5 10.5V7.75a4.5 4.5 0 0 1 9 0v2.75" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        ),
      },
      {
        label: "Real-time updates",
        icon: (
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
      },
      {
        label: "Fast & simple sign-in",
        icon: (
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M13 3 4.5 13.5H11L10 21l9-11h-6.5L13 3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          </svg>
        ),
      },
      {
        label: "Event attendance & student tracking",
        icon: (
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="4" y="3.5" width="16" height="17" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="m8 11 2.5 2.5L16 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M8 16.5h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        ),
      },
    ],
  },
  team: {
    eyebrow: "Internal Team",
    headingLead: "Everything your team needs,",
    headingHighlight: "all in one place",
    copy: "Sign in with your Search Education staff email and password to manage leads, track events, and support students end-to-end.",
    features: [
      {
        label: "Event & lead tracking",
        icon: (
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 4h16l-6.2 7.4V18l-3.6 1.8v-8.4L4 4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          </svg>
        ),
      },
      {
        label: "Comments on every student",
        icon: (
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="3" y="4.5" width="18" height="12" rx="3" stroke="currentColor" strokeWidth="1.6" />
            <path d="M8 16.5v3.2L12.5 16.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
      },
      {
        label: "University verification",
        icon: (
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 3.5 19 6.5v5c0 4.5-3 8-7 9.5-4-1.5-7-5-7-9.5v-5L12 3.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            <path d="m9 12 2 2 4-4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
      },
      {
        label: "Course finder — all in one place",
        icon: (
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="10.5" cy="10.5" r="6" stroke="currentColor" strokeWidth="1.6" />
            <path d="m19.5 19.5-4.3-4.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        ),
      },
    ],
  },
};

export default function Login() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // const { eventid } = useParams();
  const [Guest_Email, setGuest_Email] = useState("");
  const [Guest_Token, setGuest_Token] = useState("");
  const [showToken, setShowToken] = useState(false); // display-only toggle, does not touch submitted data
  const [activeField, setActiveField] = useState(null); // 'email' | 'password' | null — drives the mascot only

  // The single switch that used to be two separate pages/routes: a guest login
  // (loginGuestUser) and an internal-team/CRM login (loginCrmGuestUser). Flipping
  // it swaps both which action gets dispatched on submit AND the left-panel copy —
  // same form fields, same functionality, two different login accesses.
  const [loginMode, setLoginMode] = useState("guest"); // "guest" | "team"
  const [lastAttemptMode, setLastAttemptMode] = useState(null); // which mode the current loading/error belongs to

  const guestUserLogin = useSelector((state) => state.guestUserLogin);
  const { loading, userInfo, error } = guestUserLogin;

  // Only show the redux error if it belongs to the mode currently selected —
  // flipping the switch after a failed attempt clears the old message instead
  // of showing a stale "wrong password" from the other login type.
  const finalError = error && lastAttemptMode === loginMode ? error : null;
  const isChecking = loading && lastAttemptMode === loginMode;

  const content = BRAND_CONTENT[loginMode];

  // purely cosmetic: how far the mascot's pupils shift while "reading" the email field
  const pupilShift = Math.min(Guest_Email.length, 12) * 1.1;

  const mascotHint = finalError
    ? "Oops — please double-check your email and password."
    : isChecking
    ? loginMode === "guest"
      ? "Checking your guest access…"
      : "Checking your team access…"
    : activeField === "email"
    ? loginMode === "guest"
      ? "Type the email you registered with Search Education."
      : "Type your Search Education staff email."
    : activeField === "password"
    ? "Keep it secret! We'll close our eyes while you type."
    : loginMode === "guest"
    ? "Hi! Enter your Email & Token to continue."
    : "Welcome back! Enter your team email & password.";

  const handleSubmit = (e) => {
    e.preventDefault();
    setLastAttemptMode(loginMode);
    if (loginMode === "guest") {
      dispatch(
        loginGuestUser({
          Guest_Email: Guest_Email.trim(),
          Guest_Token: Guest_Token.trim(),
        })
      );
    } else {
      dispatch(
        loginCrmGuestUser({
          Username: Guest_Email.trim(),
          Password: Guest_Token.trim(),
        })
      );
    }
  };

  useEffect(() => {
    // console.log(userInfo)
    if (userInfo == undefined || userInfo == "") {
    } else {
      navigate(`/${id}`);
    }
  }, [dispatch, userInfo]);

  return (
    <section className="guest-login">
      <div className="guest-login-shell">
        {/* ---------- left brand panel (hidden on small screens) ---------- */}
        <div className="guest-brand-panel">
          <div className="guest-brand-bg" aria-hidden="true">
            <span className="orb orb-1"></span>
            <span className="orb orb-2"></span>
            <span className="orb orb-3"></span>
            <span className="orb orb-4"></span>
            <div className="sparkles">
              {Array.from({ length: 18 }).map((_, i) => (
                <i key={i} className={`sparkle sparkle-${(i % 6) + 1}`}></i>
              ))}
            </div>
          </div>

          {/* `key` remounts this block on flip, replaying the fade-up entrance */}
          <div className="guest-brand-content" key={loginMode}>
            <img
              src={"https://www.searcheducation.in/logo-white.svg"}
              className="guest-brand-logo"
              alt="Search Education Logo"
            />
            <span className="guest-eyebrow">{content.eyebrow}</span>
            <h2>
              {content.headingLead}{" "}
              <span className="guest-highlight">{content.headingHighlight}</span>
            </h2>
            <p className="guest-brand-copy">{content.copy}</p>

            <ul className="guest-feature-list">
              {content.features.map((feature) => (
                <li key={feature.label}>
                  {feature.icon}
                  <span>{feature.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ---------- right form panel ---------- */}
        <div className="guest-form-panel">
          <div className="guest-login-card">
            <img
              src={SE_LOGO}
              className="guest-card-logo"
              alt="Search Education Logo"
            />

            {/* ---------- Guest / Team switch ---------- */}
            <div className="guest-mode-switch" role="tablist" aria-label="Login type">
              <span
                className={`guest-mode-thumb${loginMode === "team" ? " is-team" : ""}`}
                aria-hidden="true"
              />
              <button
                type="button"
                role="tab"
                aria-selected={loginMode === "guest"}
                className={`guest-mode-tab${loginMode === "guest" ? " is-active" : ""}`}
                onClick={() => setLoginMode("guest")}
              >
                Guest
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={loginMode === "team"}
                className={`guest-mode-tab${loginMode === "team" ? " is-active" : ""}`}
                onClick={() => setLoginMode("team")}
              >
                Internal Team
              </button>
            </div>

            {/* ---------- animated mascot + hint bubble (purely visual) ---------- */}
            

            <h1>
              Sign-in{" "}
              <span className="guest-highlight">
                {loginMode === "guest" ? "Guest" : "Team"}
              </span>
            </h1>
            <p className="guest-subtitle">
              {loginMode === "guest"
                ? "Access to your guest dashboard"
                : "Access to your internal dashboard"}
            </p>
            <div className="guest-mascot-wrap" aria-hidden="true">
              <div className="guest-hint-bubble" role="status" aria-live="polite">
                {mascotHint}
              </div>
              <svg
                className={`guest-mascot-svg${finalError ? " is-worried" : ""}`}
                viewBox="0 0 160 150"
              >
                <ellipse className="guest-mascot-shadow" cx="80" cy="140" rx="34" ry="6" />
                {/* feet */}
                <ellipse cx="62" cy="132" rx="9" ry="6" className="guest-mascot-foot" />
                <ellipse cx="98" cy="132" rx="9" ry="6" className="guest-mascot-foot" />
                {/* body */}
                <ellipse cx="80" cy="88" rx="54" ry="50" className="guest-mascot-body" />
                <ellipse cx="80" cy="98" rx="33" ry="31" className="guest-mascot-belly" />
                {/* ear tufts */}
                <path d="M34 46 L46 20 L58 44Z" className="guest-mascot-body" />
                <path d="M126 46 L114 20 L102 44Z" className="guest-mascot-body" />
                {/* eyes (painted before the wings so the wings can cover them) */}
                <circle cx="60" cy="78" r="19" className="guest-mascot-eye" />
                <circle cx="100" cy="78" r="19" className="guest-mascot-eye" />
                <circle
                  cx={60 + pupilShift * 0.4}
                  cy={78 + (activeField === "email" ? 4 : 0)}
                  r="8"
                  className="guest-mascot-pupil"
                />
                <circle
                  cx={100 + pupilShift * 0.4}
                  cy={78 + (activeField === "email" ? 4 : 0)}
                  r="8"
                  className="guest-mascot-pupil"
                />
                {/* beak */}
                <path d="M74 92 L86 92 L80 102Z" className="guest-mascot-beak" />
                {/* mouth: happy vs. worried */}
                {finalError ? (
                  <path d="M68 112 Q80 104 92 112" className="guest-mascot-mouth" />
                ) : (
                  <path d="M68 108 Q80 118 92 108" className="guest-mascot-mouth" />
                )}
                {/* wings — slide up to cover the eyes while typing the token */}
                <ellipse
                  cx="26"
                  cy="92"
                  rx="14"
                  ry="24"
                  className={`guest-mascot-wing guest-mascot-wing-left${
                    activeField === "password" ? " is-hiding" : ""
                  }`}
                />
                <ellipse
                  cx="134"
                  cy="92"
                  rx="14"
                  ry="24"
                  className={`guest-mascot-wing guest-mascot-wing-right${
                    activeField === "password" ? " is-hiding" : ""
                  }`}
                />
              </svg>
            </div>

            <form onSubmit={handleSubmit}>
              <ul>
                <li className="guest-field">
                  <label htmlFor="user_name">Email Address</label>
                  <div className="guest-input-wrap">
                    <svg
                      className="guest-input-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M3 6.5C3 5.67 3.67 5 4.5 5h15c.83 0 1.5.67 1.5 1.5v11c0 .83-.67 1.5-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5v-11Z"
                        stroke="currentColor"
                        strokeWidth="1.6"
                      />
                      <path
                        d="m4 6.5 8 6.2 8-6.2"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <input
                      type="email"
                      id="user_name"
                      name="user-name"
                      required
                      placeholder="you@example.com"
                      value={Guest_Email}
                      onChange={(e) => setGuest_Email(e.target.value)}
                      onFocus={() => setActiveField("email")}
                      onBlur={() => setActiveField(null)}
                    />
                  </div>
                </li>
                <li className="guest-field">
                  <label htmlFor="user_password">Password</label>
                  <div className="guest-input-wrap">
                    <svg
                      className="guest-input-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <rect
                        x="4.5"
                        y="10.5"
                        width="15"
                        height="9.5"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="1.6"
                      />
                      <path
                        d="M7.5 10.5V7.75a4.5 4.5 0 0 1 9 0v2.75"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                    <input
                      type={showToken ? "text" : "password"}
                      id="user_password"
                      name="user-password"
                      required
                      placeholder="Enter your token"
                      value={Guest_Token}
                      onChange={(e) => setGuest_Token(e.target.value)}
                      onFocus={() => setActiveField("password")}
                      onBlur={() => setActiveField(null)}
                    />
                    <button
                      type="button"
                      className="guest-toggle-visibility"
                      onClick={() => setShowToken((prev) => !prev)}
                      aria-label={showToken ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showToken ? (
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path
                            d="M3 3l18 18M10.6 10.7a2.5 2.5 0 0 0 3.4 3.5M6.6 6.9C4.5 8.3 3 10.4 2.5 12c1.4 4 5.4 7 9.5 7 1.6 0 3.1-.4 4.4-1.2M9.9 4.4A10.4 10.4 0 0 1 12 4.2c4.1 0 8.1 2.9 9.5 7-.5 1.5-1.3 2.9-2.4 4"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path
                            d="M2.5 12c1.4-4 5.4-7 9.5-7s8.1 3 9.5 7c-1.4 4-5.4 7-9.5 7s-8.1-3-9.5-7Z"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinejoin="round"
                          />
                          <circle
                            cx="12"
                            cy="12"
                            r="2.7"
                            stroke="currentColor"
                            strokeWidth="1.6"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </li>
                <li className="button">
                  {isChecking ? (
                    <ButtonSpinner />
                  ) : (
                    <button type="submit" className="get-call px-4">
                      <span>Login</span>
                      <svg
                        className="guest-btn-arrow"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M5 12h13M13 6l6 6-6 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                  {finalError ? (
                    <span className="text-danger guest-error">{finalError}</span>
                  ) : null}
                </li>
              </ul>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
